import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";

export const DropDown = ({
  refParent,
  gapTop,
  gapLeft = 10,
  gapRight = 10,
  zIndex,
  center = "window", // center in window is default
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  refParent: React.RefObject<HTMLElement>;
  gapTop?: number;
  gapLeft?: number;
  gapRight?: number;
  zIndex?: number;
  center?: "parent" | "window";
  children: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  const [panelTop, setPanelTop] = useState<number | null>(null);
  const [panelLeft, setPanelLeft] = useState<number>(gapLeft);
  const [panelRight, setPanelRight] = useState<number>(gapRight);

  const centerOnParent = center === "parent";

  function calcLeft(rect: DOMRect, width: number) {
    console.log("calcLeft incoming width", width);

    if (centerOnParent) {
      if (width > window.innerWidth) {
        return 0;
      }

      const parentStart = rect.left;
      const childStart = rect.left + rect.width / 2 - width / 2 + gapLeft;

      const leftOffset = childStart - parentStart;

      return leftOffset;
    } else {
      // center in the browser window, taking into account the parent element's position
      const parentStart = rect.left;
      const childStart = window.innerWidth / 2 - width / 2 + gapLeft;

      const leftOffset = childStart - parentStart;

      return leftOffset;
    }
  }

  function calcRight(rect: DOMRect, width: number) {
    if (centerOnParent) {
      if (width > window.innerWidth) {
        return 0;
      }

      const parentEnd = rect.right;
      const childEnd = rect.left + rect.width / 2 + width / 2 - gapRight;

      const rightOffset = parentEnd - childEnd;
      return rightOffset;
    } else {
      // center in the browser window, taking into account the parent element's position
      const parentEnd = rect.right;
      const childEnd = window.innerWidth / 2 + width / 2 - gapRight;

      const rightOffset = parentEnd - childEnd;
      return rightOffset;
    }
  }

  // Measures the bottom of the trigger bar so the panel sits flush beneath it.
  const updatePanelTop = useCallback(() => {
    if (refParent?.current) {
      const rect = refParent.current.getBoundingClientRect();
      const width = centerOnParent
        ? refParent.current.offsetWidth
        : window.innerWidth;

      const bottom = rect.height;
      const left = calcLeft(rect, width);
      const right = calcRight(rect, width);

      console.log("Calculated left:", left, "Calculated right:", right);

      console.log("Parent rect:", rect);
      console.log("Calculated bottom:", bottom);

      setPanelTop(bottom);
      setPanelLeft(left);
      setPanelRight(right);
    }
  }, [refParent, centerOnParent]);

  // Re-measure on scroll and resize while the panel is open so it stays
  // aligned with sticky or fixed nav bars.
  useEffect(() => {
    updatePanelTop();
    window.addEventListener("resize", updatePanelTop);

    return () => {
      window.removeEventListener("resize", updatePanelTop);
    };
  }, [updatePanelTop]);

  if (panelTop === null) {
    return null;
  }

  return (
    <>
      {/* ── Popup panel, absolute positioning relative to the parent element ──────────── */}
      <div
        className={classNames(
          "tw:absolute tw:bg-transparent tw:max-w-[1280px]",
        )}
        style={{
          top: panelTop,
          left: panelLeft,
          right: panelRight,
          margin: "0 auto",
          zIndex: zIndex,
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Invisible bridge so mouse can travel from button → panel without closing it */}
        <div
          className="tw:bg-transparent tw:w-full"
          style={gapTop ? { height: `${gapTop}px` } : undefined}
        />

        {children}
      </div>
    </>
  );
};
